howspace.components.slider = (() => {
    'use strict';

    const handleTickClick = e => {
        const tick = e.target;
        const slider = tick.closest('.howspace-slider');
        const range = slider.querySelector('.howspace-slider-input');

        if (slider.classList.contains('disabled')) {
            return;
        }

        range.value = parseFloat(tick.innerText);
        howspace.dispatchEvent(range, 'input', {value: range.value});
        checkDeselected(slider);
        fillRange(slider);
    };

    const handleSliderInput = e => {
        const slider = e.currentTarget.closest('.howspace-slider');
        handleSliderValueChange(slider);
    };

    const handleSliderPointerDown = e => {
        const slider = e.currentTarget.closest('.howspace-slider');
        slider.classList.add('dragging');
        handleSliderValueChange(slider);
    };

    const handleSliderPointerUp = e => {
        const slider = e.currentTarget.closest('.howspace-slider');
        slider.classList.remove('dragging');
        hideTooltip(slider);
    };

    const handleSliderFocus = e => {
        const slider = e.currentTarget.closest('.howspace-slider');
        showTooltip(slider);
    };

    const handleSliderBlur = e => {
        const slider = e.currentTarget.closest('.howspace-slider');
        hideTooltip(slider);
    };

    const showTooltip = slider => {
        if (
            slider.classList.contains('dragging') ||
            getComputedStyle(slider).getPropertyValue('--has-visible-focus') === 'true'
        ) {
            // Pointer is down or the slider has visible focus.
            const sliderInput = slider.querySelector('.howspace-slider-input');
            const tick = slider.querySelector('.howspace-slider-tick[data-value="' + sliderInput.value + '"]');
            tick?.classList.add('show-tooltip');
        }
    };

    const hideTooltip = slider => {
        const tick = slider.querySelector('.howspace-slider-tick.show-tooltip');
        tick?.classList.remove('show-tooltip');
    };

    const handleSliderValueChange = slider => {
        checkDeselected(slider);
        fillRange(slider);
        handlePercentage(slider);
        hideTooltip(slider);
        showTooltip(slider);

        howspace.dispatchEvent(slider, howspace.components.slider.SLIDER_VALUE_CHANGE);
    };

    const checkDeselected = slider => {
        if (!slider.classList.contains('deselected') && slider.dataset.allowDeselect !== 'true') {
            return;
        }

        const sliderInput = slider.querySelector('.howspace-slider-input');
        if (sliderInput.value === sliderInput.min) {
            slider.classList.add('deselected');
            slider.dataset.skipFromRealForm = 'true';
        } else {
            slider.classList.remove('deselected');
            slider.dataset.skipFromRealForm = 'false';
        }
    };

    const fillRange = slider => {
        const sliderInput = slider.querySelector('.howspace-slider-input');
        const min = parseFloat(sliderInput.min);
        const max = parseFloat(sliderInput.max);
        const step = parseFloat(sliderInput.step);
        const totalSteps = (max - min) / step;
        const value = parseFloat(sliderInput.value);
        const progressFloat = (value - min) / totalSteps / step;
        const progressPercentage = progressFloat * 100;

        const thumbValueElem = slider.querySelector('.howspace-slider-thumb-value');
        if (thumbValueElem.innerText) {
            // Update the value element position.
            const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const thumbWidth = parseFloat(getComputedStyle(slider).getPropertyValue('--thumb-size')) * rootFontSize;
            const reduceThumbWidth = progressFloat * thumbWidth;
            slider.style.setProperty('--thumb-left-position', 'calc(' + progressPercentage + '% - ' + reduceThumbWidth + 'px)');

            // Update the value element text.
            thumbValueElem.innerText = value;
        }

        let leftPosition = 0;
        if (progressPercentage >= leftPosition) {
            // Selected value is zero or above.
            slider.style.setProperty('--track-fill-left-position', leftPosition + '%');
            slider.style.setProperty('--track-fill-right-position', 100 - progressPercentage + '%');
        } else {
            // Selected value is negative.
            slider.style.setProperty('--track-fill-left-position', leftPosition - (Math.abs(value) / totalSteps / step * 100) + '%');
            slider.style.setProperty('--track-fill-right-position', 100 - leftPosition + '%');
        }
    };

    const handlePercentage = slider => {
        if (!slider.classList.contains('percentage-mode')) {
            return;
        }

        // Deactivate all ticks.
        slider.querySelector('.howspace-slider-tick.active')?.classList.remove('active');

        // Activate the tick that corresponds with the current value (if any).
        const sliderInput = slider.querySelector('.howspace-slider-input');
        const tick = slider.querySelector('.howspace-slider-tick[data-value="' + sliderInput.value + '"]');
        tick?.classList.add('active');
    };

    return {
        SLIDER_VALUE_CHANGE: 'howspace-slider-value-change',
        init: (elem) => {
            elem.findIncludingSelf('.howspace-slider:not(.howspace-slider-initialized)').each(function () {
                const slider = $(this);
                const sliderInput = slider.find('.howspace-slider-input');

                slider.addClass('howspace-slider-initialized');
                slider.on('click', '.howspace-slider-tick', handleTickClick);
                sliderInput.on('input', handleSliderInput);
                sliderInput.on('pointerdown', handleSliderPointerDown);
                sliderInput.on('pointerup', handleSliderPointerUp);
                sliderInput.on('focus', handleSliderFocus);
                sliderInput.on('blur', handleSliderBlur);

                if (!slider.hasClass('deselected')) {
                    handleSliderValueChange(slider[0]);
                }
            });
        },
        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-slider.howspace-slider-initialized').each(function () {
                const slider = $(this);
                const sliderInput = slider.find('.howspace-slider-input');

                slider.removeClass('howspace-slider-initialized');
                sliderInput.off('input', handleSliderInput);
                sliderInput.off('pointerdown', handleSliderPointerDown);
                sliderInput.off('pointerup', handleSliderPointerUp);
                sliderInput.off('focus', handleSliderFocus);
                sliderInput.off('blur', handleSliderBlur);
            });
        },
        isDeselected: (slider) => {
            if (slider instanceof jQuery) {
                slider = slider[0];
            }
            return slider.classList.contains('deselected');
        },
        setValue: (slider, value, showThumbValue = false) => {
            if (slider instanceof jQuery) {
                slider = slider[0];
            }
            if (!slider) {
                // Slider element doesn't exist in the DOM (e.g. results pushed for a
                // filter value that wasn't present when the widget was rendered).
                return;
            }
            const sliderInput = slider.querySelector('.howspace-slider-input');
            sliderInput.value = value ?? sliderInput.min;

            if (showThumbValue) {
                slider.classList.add('big-thumb');

                const thumbValue = slider.querySelector('.howspace-slider-thumb-value');
                if (thumbValue) {
                    thumbValue.innerText = value;
                }
            } else {
                slider.classList.remove('big-thumb');

                const thumbValue = slider.querySelector('.howspace-slider-thumb-value');
                if (thumbValue) {
                    thumbValue.innerText = '';
                }
            }
            howspace.dispatchEvent(sliderInput, 'input', {value: value});
        }
    };

})();
